import { Server } from "@cloudserver/contracts";
import { api, ApiProblem } from "../api/client.js";
import { resumeConsole } from "../pages/server/console/connection.js";
import { resumeRdp } from "../rdp/connection.js";
import { resumeTerminal } from "./connections.js";
import {
  browserSessions,
  parseSaved,
  persistSessions,
  restoredTab,
  type SessionStorageLike,
} from "./persist.js";
import { RESTORED, terminals, type TerminalTab } from "./store.js";

/*
 * After a reload of the page: the session tabs of this browser tab come
 * back in their old order (docs/terminal.md, "After a reload"). Terminals
 * signed in with a certificate and consoles reconnect at once, with a new
 * certificate or ticket as any reconnect; a remote desktop or a terminal
 * signed in with a password asks for it again (passwords are never kept);
 * the Autopilot's access to a terminal starts at «Выключено», as in any new
 * session. A tab whose server is gone says so and keeps only its ✕.
 */

/**
 * A tab that does not reconnect by itself asks the API whether its server is
 * still there (the others learn it from their own attempt to connect).
 */
async function checkServer(tab: TerminalTab): Promise<void> {
  try {
    await api(`/projects/${tab.projectId}/servers/${tab.serverId}`, { schema: Server });
  } catch (error) {
    if (error instanceof ApiProblem && (error.status === 404 || error.status === 403))
      terminals.update(tab.id, { problem: error.problem?.code ?? "not_found" });
  }
}

let started = false;

/**
 * Brings this browser tab's sessions back once per page and keeps them from
 * then on; only the person's own (`userId`). Called by the signed-in shell.
 */
export function restoreSessions(userId: string, storage: SessionStorageLike = browserSessions) {
  if (started) return;
  started = true;
  const saved = parseSaved(storage.read(), userId);
  if (saved && saved.tabs.length > 0) {
    const tabs = saved.tabs.map(restoredTab);
    terminals.restore(tabs, saved);
    for (const tab of tabs) {
      if (tab.kind === "rdp") resumeRdp(tab.id);
      else if (tab.kind === "vnc") resumeConsole(tab.id);
      else resumeTerminal(tab.id);
      if (tab.problem === RESTORED) void checkServer(tab);
    }
  }
  persistSessions(terminals, userId, storage);
}
